import React from "react";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@apollo/client";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { MyHrUnlockForm, MyHrUnlockSSOForm } from "my-hr/forms";
import { useMyHrEmployee } from "my-hr/hooks";
import { OcAvatar, OcTransition } from "oc/components";
import { OC_PROPERTY_SUBSCRIPTION } from "oc/graphql";
import { useOcCurrentUserState, useOcIsMobile, useOcProperty } from "oc/hooks";
import { getFullname } from "oc/utils";

type MyHrUserSessionLockedPageProps = {
  sessionLocked: boolean;
  openSession: (loginResult?: any) => void;
  onLogout: () => void;
};

export const MyHrUsserSessionLockedPage = ({
  sessionLocked,
  openSession,
  onLogout,
}: MyHrUserSessionLockedPageProps) => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  let { employeeCode, emailAddress, ldapUser } = useOcCurrentUserState();
  const { data } = useMyHrEmployee({ employeeId: employeeCode });

  let fullname = "";
  if (data) {
    fullname = getFullname(data) ?? "";
  }

  const { data: loginTypeData } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  useSubscription(OC_PROPERTY_SUBSCRIPTION, {
    variables: {
      resource: "system",
      key: "loginType",
    },
  });

  const isSSO = loginTypeData === "sso" ? true : false;

  return (
    <Dialog
      open={sessionLocked ? sessionLocked : false}
      scroll={"body"}
      fullScreen={isMobile}
      TransitionComponent={OcTransition}
      //disableBackdropClick
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          return false;
        }
      }}
      BackdropProps={{
        style: {
          width: "100%",
          backgroundColor: "rgba(0,0,0,0.7",
          backgroundImage: "url(/img/loginBg.jpg)",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundBlendMode: "darken",
        },
      }}
    >
      <Box
        width={isMobile ? "auto" : "480px"}
        height={isMobile ? "100%" : "auto"}
        padding={isMobile ? "0px 22px" : "36px 22px"}
        id="session-locked-dialog-content"
        display="flex"
        flexDirection="column"
        justifyContent={isMobile ? "center" : "flex-start"}
      >
        <Box
          id="inner-content"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Box
            id="avatar-container"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            paddingRight="24px"
          >
            <OcAvatar
              employee={data?.code}
              style={{ width: "72px", height: "72px" }}
            />
          </Box>
          <Box>
            <Box paddingBottom="28px">
              <Typography variant="h6">{t("MY_HR_TITLE_UNLOCK")}</Typography>
            </Box>
            <Box>
              <Typography variant="body1" color="#5E6366">
                {t("MY_HR_TEXT_UNLOCK")}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box paddingTop="32px">
          {isSSO ? (
            <MyHrUnlockSSOForm
              onSubmit={openSession}
              buttonLabel={t("MY_HR_UNLOCK")}
              username={ldapUser}
            />
          ) : (
            <MyHrUnlockForm
              onSubmit={openSession}
              buttonLabel={t("MY_HR_UNLOCK")}
              email={emailAddress}
            />
          )}
          <Box
            onClick={onLogout}
            style={{
              color: "#cc0033",
              fontSize: 16,
              letterSpacing: 0.44,
              cursor: "pointer",
              textAlign: "center",
              paddingTop: "36px",
            }}
          >
            {t("MY_HR_ESCAPE_TEXT").replace("%fullname%", fullname)}
          </Box>
          <Box paddingTop="24px">
            <Button
              fullWidth
              onClick={onLogout}
              variant="text"
              color="secondary"
            >
              {t("MY_HR_LOGOUT")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MyHrUsserSessionLockedPage;
